import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, FileCheck2, ExternalLink, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Input } from '@/components/ui/input.jsx';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import CrmContactPicker from './CrmContactPicker.jsx';
import PdfViewerDialog from './PdfViewerDialog.jsx';
import { useLicense, useLicenseMe } from '@/hooks/useLicenses.js';
import { licenseStatus, fmtDate } from '@/lib/license-status.js';
import { stateName } from '@/lib/us-states.js';
import {
  updateAgencyLicense, updateProducerLicense,
  deleteAgencyLicense, deleteProducerLicense, uploadLicensePdf,
} from '@/services/licenses.service.js';

const EDITABLE = ['license_class', 'issue_date', 'expiration_date', 'renewal_fee_cents', 'state_portal_url'];

export default function LicenseDetail({ kind }) {
  const isAgency = kind === 'agency';
  const { id } = useParams();
  const navigate = useNavigate();
  const me = useLicenseMe();
  const { row, error, loading, refresh } = useLicense(kind, id);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [drlp, setDrlp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewPdf, setViewPdf] = useState(false);

  useEffect(() => {
    if (row) {
      setForm({
        license_class: row.license_class || '', issue_date: row.issue_date || '',
        expiration_date: row.expiration_date || '',
        renewal_fee_cents: row.renewal_fee_cents != null ? (row.renewal_fee_cents / 100).toString() : '',
        state_portal_url: row.state_portal_url || '',
        npn: row.npn || '', is_resident: !!row.is_resident,
      });
      setDrlp(row.drlp_broker_contact_id ? { id: row.drlp_broker_contact_id, name: row.drlp_name_snapshot } : null);
    }
  }, [row]);

  if (loading && !row) return <Shell><div className="text-sm text-text-muted">Loading…</div></Shell>;
  if (error || !row) return <Shell><div className="text-sm text-accent">Couldn't load this license.</div></Shell>;

  const st = licenseStatus(row.expiration_date);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  function buildPatch() {
    const b = {};
    for (const k of EDITABLE) {
      if (k === 'renewal_fee_cents') {
        b.renewal_fee_cents = form.renewal_fee_cents === '' ? null : Math.round(parseFloat(form.renewal_fee_cents) * 100);
      } else { b[k] = form[k] === '' ? null : form[k]; }
    }
    if (isAgency) {
      b.drlp_broker_contact_id = drlp?.id || null;
      b.drlp_name_snapshot = drlp?.name || null;
    } else {
      b.npn = form.npn === '' ? null : form.npn;
      b.is_resident = !!form.is_resident;
    }
    return b;
  }

  async function save() {
    setSaving(true);
    try {
      const fn = isAgency ? updateAgencyLicense : updateProducerLicense;
      const res = await fn(id, buildPatch());
      const warn = res._warnings?.[0];
      toast.success(warn ? `Saved — ${warn}` : 'Saved');
      setEditing(false); refresh();
    } catch (err) {
      toast.error(`Save failed: ${err.body?.errors?.[0] || err.body?.error || err.code || err.message}`);
    } finally { setSaving(false); }
  }

  async function onFile(file) {
    if (!file) return;
    try {
      await uploadLicensePdf(kind, id, file);
      toast.success(row.workdrive_file_id ? 'PDF replaced (prior kept as superseded)' : 'PDF uploaded');
      refresh();
    } catch (err) { toast.error(`Upload failed: ${err.message}`); }
  }

  async function doDelete() {
    setDeleting(true);
    try {
      await (isAgency ? deleteAgencyLicense(id) : deleteProducerLicense(id));
      toast.success('License deleted');
      navigate('/admin/licenses?tab=' + kind);
    } catch (err) {
      toast.error(`Delete failed: ${err.body?.error || err.message}`);
      setDeleting(false);
    }
  }

  const drlpWarn = row._warnings?.[0];

  return (
    <Shell>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-text">{stateName(row.state)} · {row.license_number}</h2>
            <Badge variant={st.variant}>{st.label}</Badge>
          </div>
          <p className="mt-1 text-sm text-text-muted">
            {isAgency ? (row.agency_dba || row.agency_legal_name) : row.broker_name_snapshot}
            {row.license_class ? ` · ${row.license_class}` : ''}
          </p>
        </div>
        {!editing
          ? <Button variant="outline" onClick={() => setEditing(true)}>Edit</Button>
          : <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setEditing(false); }}>Cancel</Button>
              <Button disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>}
      </div>

      {drlpWarn && <div className="mt-3 rounded-card border border-warning/40 bg-warning-soft p-2.5 text-xs text-warning">{drlpWarn}</div>}

      {/* Metadata */}
      <div className="mt-5 grid grid-cols-2 gap-4">
        <Row label="License class" editing={editing}><Input value={form.license_class} onChange={set('license_class')} /></Row>
        <Static label="License class" value={row.license_class || '—'} editing={editing} />
        <Row label="Issue date" editing={editing}><Input type="date" value={form.issue_date} onChange={set('issue_date')} /></Row>
        <Static label="Issued" value={fmtDate(row.issue_date)} editing={editing} />
        <Row label="Expiration date" editing={editing}><Input type="date" value={form.expiration_date} onChange={set('expiration_date')} /></Row>
        <Static label="Expires" value={fmtDate(row.expiration_date)} editing={editing} />
        <Row label="Renewal fee ($)" editing={editing}><Input type="number" min="0" step="0.01" value={form.renewal_fee_cents} onChange={set('renewal_fee_cents')} /></Row>
        <Static label="Renewal fee" value={row.renewal_fee_cents != null ? `$${(row.renewal_fee_cents / 100).toFixed(2)}` : '—'} editing={editing} />
        <Row label="State portal URL" editing={editing}><Input value={form.state_portal_url} onChange={set('state_portal_url')} /></Row>
        <Static label="State portal" value={row.state_portal_url
          ? <a href={row.state_portal_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent">portal <ExternalLink size={12} /></a>
          : '—'} editing={editing} />

        {isAgency ? (
          <>
            {editing
              ? <div className="col-span-2"><FieldLabel>DRLP</FieldLabel><CrmContactPicker value={drlp} onSelect={setDrlp} placeholder="Search DRLP…" /></div>
              : <Static label="DRLP" value={row.drlp_name_snapshot || '—'} editing={editing} />}
          </>
        ) : (
          <>
            <Row label="NPN" editing={editing}><Input value={form.npn} onChange={set('npn')} /></Row>
            <Static label="NPN" value={row.npn || '—'} editing={editing} />
            {editing
              ? <div><FieldLabel>Resident?</FieldLabel><label className="flex h-11 items-center gap-2 text-sm"><input type="checkbox" checked={form.is_resident} onChange={(e) => setForm((p) => ({ ...p, is_resident: e.target.checked }))} /> Resident</label></div>
              : <Static label="Resident?" value={row.is_resident ? 'Yes' : 'No'} editing={editing} />}
          </>
        )}
      </div>

      {/* Document */}
      <div className="mt-6">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">License PDF</h3>
        {row.workdrive_file_id ? (
          <div className="flex items-center justify-between gap-3 rounded-card border border-border bg-surface p-3">
            <button type="button" onClick={() => setViewPdf(true)} className="flex items-center gap-2 text-sm text-text hover:text-accent">
              <FileCheck2 size={18} className="text-success" /> <span className="underline-offset-2 hover:underline">{row.workdrive_file_name || 'license.pdf'}</span>
            </button>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setViewPdf(true)} className="text-sm text-accent hover:underline">View</button>
              <label htmlFor="repl" className="cursor-pointer text-sm text-accent hover:underline">Replace
                <input id="repl" type="file" accept=".pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              </label>
            </div>
          </div>
        ) : (
          <label htmlFor="repl" className="flex min-h-[88px] cursor-pointer items-center justify-center rounded-card border-2 border-dashed border-border bg-bg/50 p-3 text-center hover:border-accent/40"
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }} onDragOver={(e) => e.preventDefault()}>
            <span className="flex flex-col items-center text-xs text-text-muted">
              <Upload size={20} strokeWidth={1.5} className="mb-1 text-text-dim" />
              Drop the license PDF or <span className="text-accent">browse</span>
            </span>
            <input id="repl" type="file" accept=".pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
        )}
      </div>

      {/* Danger zone */}
      <div className="mt-8 border-t border-border pt-4">
        <Button variant="destructive" onClick={() => setConfirmDel(true)}><Trash2 size={15} strokeWidth={1.5} /> Delete license</Button>
      </div>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {row.state} license?</AlertDialogTitle>
            <AlertDialogDescription>
              Hard-deletes this row (the audit trail stays in the activity log; the WorkDrive PDF is not removed). You can re-create it from the PDF.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Back</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PdfViewerDialog open={viewPdf} onOpenChange={setViewPdf} kind={kind} id={id} title={`${stateName(row.state)} — ${row.license_number}`} />
    </Shell>
  );
}

function Shell({ children }) {
  const navigate = useNavigate();
  return (
    <div className="p-4">
      <button type="button" onClick={() => navigate(-1)} className="mb-3 flex items-center gap-1 text-sm text-text-muted hover:text-text">
        <ArrowLeft size={16} strokeWidth={1.5} /> Back
      </button>
      {children}
    </div>
  );
}

function FieldLabel({ children }) {
  return <label className="mb-1.5 block text-xs font-medium text-text-muted">{children}</label>;
}
// In edit mode show the input; in view mode the Row collapses (its Static sibling shows).
function Row({ label, editing, children }) {
  if (!editing) return null;
  return <div><FieldLabel>{label}</FieldLabel>{children}</div>;
}
function Static({ label, value, editing }) {
  if (editing) return null;
  return (
    <div>
      <div className="text-xs text-text-muted">{label}</div>
      <div className="mt-0.5 text-sm text-text">{value}</div>
    </div>
  );
}
